import styled, { keyframes } from 'styled-components';

const MOBILE = '@media (max-width: 768px)';
const CARD_MIN_WIDTH = '20rem';
const CARD_MAX_WIDTH = '24rem';
const ARROW_BOB_DURATION = '1.6s';

const arrowBob = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(0.4rem);
  }
`;

const arrowBobDown = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(0.4rem);
  }
`;

export const LoopCard = styled.div`
  margin-top: 4.8rem;
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.accentBorder};
  border-radius: 2rem;
  padding: 3.2rem 2.4rem;
  box-shadow:
    0 1.6rem 3.2rem rgba(15, 23, 42, 0.06),
    0 0 0 0.1rem rgba(15, 23, 42, 0.02);

  ${MOBILE} {
    margin-top: 3.2rem;
    padding: 2.4rem 1.6rem;
  }
`;

export const LoopHeader = styled.div`
  text-align: center;
  margin-bottom: 2.8rem;

  ${MOBILE} {
    margin-bottom: 2rem;
  }
`;

export const LoopKicker = styled.div`
  display: inline-block;
  font-size: 1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent};
  background-color: ${({ theme }) => theme.colors.accentLight};
  border-radius: 999rem;
  padding: 0.4rem 1.2rem;
  text-transform: uppercase;
  letter-spacing: 0.15rem;
  margin-bottom: 1rem;
`;

export const LoopTitle = styled.h3`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin: 0 0 0.8rem;
  letter-spacing: -0.04rem;
  line-height: 1.2;

  ${MOBILE} {
    font-size: 2rem;
  }
`;

export const LoopSub = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  line-height: 1.5;
  max-width: 48rem;
  margin: 0 auto;
`;

export const Diagram = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 1.6rem;

  ${MOBILE} {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

export const ArrowCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.8rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent};

  & > span {
    display: inline-block;
    animation: ${arrowBob} ${ARROW_BOB_DURATION} ease-in-out infinite;
  }

  ${MOBILE} {
    font-size: 2.2rem;
    & > span {
      animation-name: ${arrowBobDown};
    }
  }
`;

export const ReviewCard = styled.div`
  flex: 1;
  min-width: ${CARD_MIN_WIDTH};
  max-width: ${CARD_MAX_WIDTH};
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1.4rem;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  ${MOBILE} {
    width: 100%;
    max-width: 32rem;
  }
`;

export const ReviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.successDark};
  background-color: ${({ theme }) => theme.colors.successBgSoft};
  border: 0.1rem solid ${({ theme }) => theme.colors.successBorder};
  border-radius: 999rem;
  padding: 0.2rem 0.7rem;
`;

export const ReviewStars = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.amberSolid};
  letter-spacing: 0.1rem;
`;

export const ReviewQuote = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  line-height: 1.5;
  margin: 0;
`;

export const ReviewMeta = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: auto;
`;

export const PostCard = styled.div`
  flex: 1;
  min-width: ${CARD_MIN_WIDTH};
  max-width: ${CARD_MAX_WIDTH};
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1.4rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${MOBILE} {
    width: 100%;
    max-width: 32rem;
  }
`;

export const PostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.9rem 1.2rem;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border};
`;

export const PostAvatar = styled.div`
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  padding: 0.2rem;
  background: linear-gradient(135deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PostAvatarInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const PostHandle = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

export const PostName = styled.span`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const PostSub = styled.span`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const PostBody = styled.div`
  position: relative;
  padding: 2rem 1.4rem;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.accentLight} 0%,
    ${({ theme }) => theme.colors.accentBgSoft} 100%
  );
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  flex: 1;
`;

export const PostQuoteMark = styled.div`
  position: absolute;
  top: 0.4rem;
  left: 1rem;
  font-family: Georgia, serif;
  font-size: 4.4rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.accent};
  opacity: 0.35;
  pointer-events: none;
`;

export const PostQuote = styled.p`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  line-height: 1.45;
  font-weight: 600;
  margin: 0;
  position: relative;
  z-index: 1;
`;

export const PostAttribution = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.accentTextDark};
  font-weight: 700;
  margin-top: auto;
`;

export const PostFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1.2rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const PostLink = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 700;
`;
